<template>
  <div class="wrapper">
    <FORM ref="childForm" @formclick="formclick" :formData="formData"></FORM>
    
    <div class="top-scrollbar" ref="topScroll" @scroll="handleTopScroll">
      <div class="top-scrollbar-content" :style="{ width: tableContentWidth + 'px' }"></div>
    </div>

    <table-page ref="childTable" :pageData="pageData" @getTableData="handleTableData"></table-page>
  </div>
</template>

<script>
import tablePage from '@/components/page/table.vue'
import FORM from '@/components/page/form.vue'
import URL from '@/views/analyse/util/url'

// 日期格式化小工具 (強制轉為 UTC+8 並產出 yyyy-MM-dd HH:mm:ss)
const formatUTC8 = (data) => {
  if (!data) return '';
  const date = new Date(data);
  if (isNaN(date.getTime())) return data;
  
  const utc = date.getTime() + (date.getTimezoneOffset() * 60000);
  const utc8Time = new Date(utc + 28800000);
  
  const pad = (n) => (n < 10 ? '0' + n : n);
  return `${utc8Time.getFullYear()}-${pad(utc8Time.getMonth() + 1)}-${pad(utc8Time.getDate())} ${pad(utc8Time.getHours())}:${pad(utc8Time.getMinutes())}:${pad(utc8Time.getSeconds())}`;
};

export default {
  name: 'RiskAnalyseList',
  components: {
    tablePage,
    FORM
  },
  data() {
    return {
      tableContentWidth: 0,
      activeScrollSource: null, // 記錄目前是誰在主動滾動 ('top' 或 'bottom')
      scrollTimer: null,        // 判定滾動結束的計時器
      formData: {
        initClickName: window.$t('analyse.bt.query'),
        hasLable: true,
        cols: [
          {
            title: window.$t('analyse.riskAnalyse.queryDays'), 
            field: 'queryDays', 
            type: 2, 
            optionList: [{ key: 30, value: window.$t('analyse.riskAnalyse.thirtyDays') }, { key: 60, value: window.$t('analyse.riskAnalyse.sixtyDays') }], 
            data: 30,
            isNumber: true
          },
          {
            title: window.$t('analyse.riskAnalyse.isSharedIp'), 
            field: 'isSharedIp', 
            type: 2, 
            optionList: [{ key: true, value: window.$t('analyse.riskAnalyse.yes') }, { key: false, value: window.$t('analyse.riskAnalyse.no') }], 
            data: false
          },
          { 
            title: window.$t('analyse.riskAnalyse.openIp'), 
            field: 'openIp', 
            type: 1, 
            placeholder: window.$t('analyse.riskAnalyse.ipPlaceholder') 
          },
          { 
            title: window.$t('analyse.riskAnalyse.shortTermTradeRatioThreshold'), 
            field: 'shortTermTradeRatioThreshold', 
            type: 1, 
            placeholder: window.$t('analyse.riskAnalyse.thresholdPlaceholder') 
          },
          { 
            title: window.$t('analyse.riskAnalyse.shortTermProfitRatioThreshold'), 
            field: 'shortTermProfitRatioThreshold', 
            type: 1, 
            placeholder: window.$t('analyse.riskAnalyse.thresholdPlaceholder') 
          },
          { click: window.$t('analyse.bt.query') },
          { click: window.$t('analyse.riskAnalyse.export'), type: 'success', icon: 'el-icon-download' } // 導出 Excel 按鈕
        ]
      },
      pageData: {
        isShow: false,
        queryUrl: URL.riskAnalyse.queryByDays,
        method: 'get',
        selection: false,
        sequence: false,
        height: 'calc(100vh - 250px)',
        cols: [
          { title: window.$t('analyse.riskAnalyse.customerNumber'), field: 'customerNumber', fixed: 'left' },
          { title: window.$t('analyse.riskAnalyse.activateTime'), field: 'activateTime', render: (data) => formatUTC8(data), fixed: 'left', sortable: true  },
          { title: window.$t('analyse.riskAnalyse.referrerNumber'), field: 'referrerNumber', render: (data) => !data ? window.$t('analyse.riskAnalyse.empty') : data, fixed: 'left'  },
          { title: window.$t('analyse.riskAnalyse.source'), field: 'source' },
          { title: window.$t('analyse.riskAnalyse.medium'), field: 'medium' },
          { title: window.$t('analyse.riskAnalyse.latestBalance'), field: 'latestBalance', fixed: 'left'  },
          { title: window.$t('analyse.riskAnalyse.latestEquity'), field: 'latestEquity', fixed: 'left'  },
          { title: window.$t('analyse.riskAnalyse.latestSettleDate'), field: 'latestSettleDate', fixed: 'left'  },
          { title: window.$t('analyse.riskAnalyse.firstTimeCashInCurrency'), field: 'firstTimeCashInCurrency' },
          { title: window.$t('analyse.riskAnalyse.totalCashIn'), field: 'totalCashIn' },
          { title: window.$t('analyse.riskAnalyse.totalCashOut'), field: 'totalCashOut'  },
          { title: window.$t('analyse.riskAnalyse.netCashIn'), field: 'netCashIn'  },
          { title: window.$t('analyse.riskAnalyse.totalTrade'), field: 'totalTrade', sortable: true },
          { title: window.$t('analyse.riskAnalyse.totalVolume'), field: 'totalVolume', sortable: true },
          { title: window.$t('analyse.riskAnalyse.totalProfit'), field: 'totalProfit', sortable: true },
          { title: window.$t('analyse.riskAnalyse.highProfitMultiple'), field: 'highProfitMultiple', sortable: true, render: (data) => data == null ? '' : Number(data).toFixed(2) },
          { title: window.$t('analyse.riskAnalyse.totalClearZero'), field: 'totalClearZero', sortable: true },
          { title: window.$t('analyse.riskAnalyse.totalCommission'), field: 'totalCommission', sortable: true }, 
          { title: window.$t('analyse.riskAnalyse.totalSwap'), field: 'totalSwap', sortable: true },
          { title: window.$t('analyse.riskAnalyse.shortTermTradeRatioPct'), field: 'shortTermTradeRatioPct', sortable: true },
          { title: window.$t('analyse.riskAnalyse.shortTermProfitRatioPct'), field: 'shortTermProfitRatioPct', sortable: true },
          { title: window.$t('analyse.riskAnalyse.top3ProfitableSymbols'), field: 'top3ProfitableSymbols', overflow: true },
          { title: window.$t('analyse.riskAnalyse.openIp'), field: 'openIp' },
          { title: window.$t('analyse.riskAnalyse.ipUsageCount'), field: 'ipUsageCount', sortable: true },
          { title: window.$t('analyse.riskAnalyse.sharedCustomerNumbers'), field: 'sharedCustomerNumbers', overflow: true },
          { title: window.$t('analyse.riskAnalyse.ipRegLocationCn'), field: 'ipRegLocationCn' },
          { title: window.$t('analyse.riskAnalyse.idRegLocationCn'), field: 'idRegLocationCn' },
          { title: window.$t('analyse.riskAnalyse.mobilePhoneRegLocationCn'), field: 'mobilePhoneRegLocationCn' }
        ]
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.bindTableScroll();
      // 監聽視窗縮放，確保寬度始終同步
      window.addEventListener('resize', this.syncWidths);
    });
  },
  beforeDestroy() {
    // 組件銷毀時移除監聽，避免記憶體洩漏
    window.removeEventListener('resize', this.syncWidths);
  },
  methods: {
    // 獨立出來的表單校驗邏輯
    validateData(data) {
      if (data.openIp) {
        const ipv4Regex = /^(25[0-5]|2[0-4]\d|[0-1]?\d?\d)(\.(25[0-5]|2[0-4]\d|[0-1]?\d?\d)){3}$/;
        if (!ipv4Regex.test(data.openIp)) {
          this.$message.error(window.$t('analyse.riskAnalyse.ipError'));
          return false;
        }
      }
      const decimalRegex = /^\d+(\.\d{1,2})?$/;
      if (data.shortTermTradeRatioThreshold && !decimalRegex.test(data.shortTermTradeRatioThreshold)) {
        this.$message.error(window.$t('analyse.riskAnalyse.tradeRatioError'));
        return false;
      }
      if (data.shortTermProfitRatioThreshold && !decimalRegex.test(data.shortTermProfitRatioThreshold)) {
        this.$message.error(window.$t('analyse.riskAnalyse.profitRatioError'));
        return false;
      }
      return true;
    },

    formclick(clickName, data) {
      if (clickName === window.$t('analyse.bt.query') || clickName === '查詢') {
        if (!this.validateData(data)) return;
        this.$nextTick(() => {
          if (this.$refs.childTable) {
            this.$refs.childTable.defaultQueryData(data);
          }
        });
      } 
      else if (clickName === window.$t('analyse.riskAnalyse.export') || clickName === '导出Excel' || clickName === '導出Excel') {
        if (!this.validateData(data)) return;
        this.handleExport(data);
      }
    },

    // 處理匯出 Excel 的邏輯
    handleExport(data) {
      this.$message.info(window.$t('analyse.riskAnalyse.exportMessage'));
      const loading = this.$loading({
        lock: true,
        text: window.$t('analyse.riskAnalyse.exporting'),
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      this.$axios({
        method: 'get',
        url: URL.riskAnalyse.export,
        params: data,
        responseType: 'blob'
      }).then(res => {
        loading.close();

        // 攔截後端拋出的 JSON 錯誤訊息
        if (res.type === 'application/json') {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const err = JSON.parse(reader.result);
              // 替換為語系檔
              this.$message.error(err.message || window.$t('analyse.riskAnalyse.exportFailed'));
            } catch (e) {
              // 替換為語系檔
              this.$message.error(window.$t('analyse.riskAnalyse.exportFailed'));
            }
          };
          reader.readAsText(res);
          return;
        }

        // 成功獲取檔案，建立 Blob
        const blob = new Blob([res], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });

        // 動態生成檔名
        const fileName = `RiskAnalyse_${formatUTC8(new Date()).replace(/[- :]/g, '')}.xlsx`;

        // 觸發瀏覽器下載
        const downloadElement = document.createElement('a');
        const href = window.URL.createObjectURL(blob);
        downloadElement.href = href;
        downloadElement.download = fileName;
        document.body.appendChild(downloadElement);
        downloadElement.click();
        
        // 清理記憶體
        document.body.removeChild(downloadElement);
        window.URL.revokeObjectURL(href);

      }).catch(err => {
        loading.close();
        console.error('Export Error:', err);
        // 替換為語系檔中的網路連線錯誤訊息
        this.$message.error(window.$t('analyse.riskAnalyse.exportNetworkError'));
      });
    },
    // 精準同步寬度
    syncWidths() {
      if (!this.$refs.childTable || !this.$refs.topScroll) return;
      
      const tableBodyWrapper = this.$refs.childTable.$el.querySelector('.el-table__body-wrapper');
      if (tableBodyWrapper) {
        const tableMaxScroll = tableBodyWrapper.scrollWidth - tableBodyWrapper.clientWidth;
        const topClientWidth = this.$refs.topScroll.clientWidth;
        this.tableContentWidth = topClientWidth + tableMaxScroll;
      }
    },
    handleTableData() {
      // 給 Element UI 渲染表格 DOM 一點時間 (100ms)，確保抓到的寬度是最終渲染結果
      setTimeout(() => {
        this.syncWidths();
      }, 100);
    },

    // 頂部滑動條捲動時，同步下方表格
    handleTopScroll(e) {
      // 如果目前是「底部表格」在主動滾動，頂部就不要發送同步指令，避免無限迴圈
      if (this.activeScrollSource === 'bottom') return;
      
      // 標記現在是「頂部」在主動滾動
      this.activeScrollSource = 'top';

      const tableBody = this.$refs.childTable.$el.querySelector('.el-table__body-wrapper');
      if (tableBody && Math.abs(tableBody.scrollLeft - e.target.scrollLeft) > 1) {
        // 零延遲直接賦值，保證絲滑同步
        tableBody.scrollLeft = e.target.scrollLeft;
      }

      // 重置防抖計時器：只要 50 毫秒內沒有新的滾動事件，就視為滾動結束，釋放主動權鎖
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.activeScrollSource = null;
      }, 50);
    },

    // 綁定下方表格，捲動時同步頂部滑動條
    bindTableScroll() {
      const tableBody = this.$refs.childTable.$el.querySelector('.el-table__body-wrapper');
      if (tableBody) {
        tableBody.addEventListener('scroll', (e) => {
          // 如果目前是「頂部」在主動滾動，底部就乖乖被動跟隨，不要發送同步指令
          if (this.activeScrollSource === 'top') return;
          
          // 標記現在是「底部表格」在主動滾動
          this.activeScrollSource = 'bottom';

          if (this.$refs.topScroll && Math.abs(this.$refs.topScroll.scrollLeft - e.target.scrollLeft) > 1) {
            // 零延遲直接賦值
            this.$refs.topScroll.scrollLeft = e.target.scrollLeft;
          }

          // 滾動結束後釋放主動權鎖
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => {
            this.activeScrollSource = null;
          }, 50);
        });
      }
    },
  }
}
</script>

<style scoped>
.wrapper {
  padding: 20px;
}

.top-scrollbar {
  width: 96%; /* 恢復與 table.vue 完全一致的寬度佔比 */
  margin: 0 auto; /* 確保與表格一起完美居中對齊 */
  overflow-x: auto;
  overflow-y: hidden;
}

.top-scrollbar::-webkit-scrollbar {
  height: 12px;
}
.top-scrollbar::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 6px;
}
.top-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 6px;
}

.top-scrollbar-content {
  height: 1px;
}
</style>